import { DestroyRef, inject, Injectable, NgZone, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, fromEvent, merge, Subject, throttleTime } from 'rxjs';

import { ACTIVITY_EVENTS, ACTIVITY_THROTTLE_MS } from './session.models';

/**
 * Tracks activity for **this tab only**. Global idleness spans tabs and is
 * computed in `SessionAuditService` by combining this signal with the lease
 * registry, because a follower tab where the user is typing must not let the
 * leader consider the user idle.
 */
@Injectable({ providedIn: 'root' })
export class UserActivityService {
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  private readonly activitySubject = new Subject<number>();
  private readonly lastActivity = signal(Date.now());

  readonly activity$ = this.activitySubject.asObservable();
  readonly lastActivityAt = this.lastActivity.asReadonly();

  constructor() {
    const streams = ACTIVITY_EVENTS.map((name) =>
      fromEvent(document, name, { passive: true, capture: true })
    );

    // Outside the zone: raw pointer and scroll events must not schedule change
    // detection. The throttled signal write is the only zone-visible effect.
    this.zone.runOutsideAngular(() => {
      merge(...streams)
        .pipe(
          // `mousemove`/`scroll` fire whenever the cursor is over this window,
          // even while a different application has focus (e.g. the browser is
          // only partially covered by another app). Without this check, a
          // stray cursor pass keeps the tab "active" while the user is really
          // working elsewhere. `hasFocus()` is checked per-event, before
          // throttling, so only genuinely focused activity resets the timer.
          filter(() => document.hasFocus()),
          throttleTime(ACTIVITY_THROTTLE_MS),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(() => this.markActive());
    });
  }

  markActive(now: number = Date.now()): void {
    this.lastActivity.set(now);
    this.activitySubject.next(now);
  }
}
